import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { parseTrendbars, mergeLiveCandle, type CandleData } from '@/lib/indicators';
import { evaluateStrategySignal, calculateLotSize, type SignalResult } from '@/lib/signal-engine';
import type { Strategy } from '@/types/strategy';

interface CtraderAccount {
  id: number;
  account_id: string;
  account_name: string;
  balance: number;
  currency: string;
  is_live: boolean;
}

interface SymbolInfo {
  symbolId: number;
  name: string;
  digits: number;
}

interface StrategySignalMonitorProps {
  strategy: Strategy;
}

const TIMEFRAME_PERIOD: Record<string, number> = {
  M1: 1, M5: 5, M15: 15, M30: 30,
  H1: 60, H4: 240, D1: 1440, W1: 10080, MN: 43200,
};

function formatPrice(value: number | null, digits = 5): string {
  if (value === null) return '\u2014';
  return value.toFixed(digits);
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

export default function StrategySignalMonitor({ strategy }: StrategySignalMonitorProps) {
  const [gatewayConnected, setGatewayConnected] = useState(false);
  const [gatewayAccounts, setGatewayAccounts] = useState<CtraderAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);

  const [symbolInfo, setSymbolInfo] = useState<SymbolInfo | null>(null);
  const [symbolLoading, setSymbolLoading] = useState(false);

  const [candles, setCandles] = useState<CandleData[]>([]);
  const [candleLoading, setCandleLoading] = useState(false);
  const [candleError, setCandleError] = useState<string | null>(null);

  const [spotPrice, setSpotPrice] = useState<{ bid: number | null; ask: number | null; timestamp: number } | null>(null);
  const [signalResult, setSignalResult] = useState<SignalResult | null>(null);

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<number>(0);

  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const spotPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Fetch gateway status ────────────────────────────────────

  const fetchGatewayStatus = useCallback(async () => {
    try {
      const { data } = await supabase.functions.invoke<{ connected: boolean; accounts: CtraderAccount[] }>('ctrader-gateway', {
        method: 'POST',
        body: { action: 'status' },
      });
      if (data) {
        setGatewayConnected(data.connected);
        setGatewayAccounts(data.accounts || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchGatewayStatus();
  }, [fetchGatewayStatus]);

  // Auto-select first account
  useEffect(() => {
    if (gatewayAccounts.length > 0 && selectedAccountId === null) {
      const firstAcc = gatewayAccounts[0];
      setSelectedAccountId(Number(firstAcc.account_id) || firstAcc.id);
    }
  }, [gatewayAccounts, selectedAccountId]);

  // ─── Resolve symbol name -> ID ────────────────────────────────

  const resolveSymbol = useCallback(async () => {
    if (!selectedAccountId || !strategy.symbol) return;
    setSymbolLoading(true);
    try {
      const { data } = await supabase.functions.invoke<{
        success: boolean;
        symbolsRes: Record<string, unknown> | null;
      }>('ctrader-gateway', {
        method: 'POST',
        body: {
          action: 'get-symbols-list',
          accountId: selectedAccountId,
          includeSymbolsWithoutTickets: true,
        },
      });

      if (data?.success && data.symbolsRes) {
        const symArray = Array.isArray(data.symbolsRes.symbol)
          ? (data.symbolsRes.symbol as Array<Record<string, unknown>>)
          : [];
        const found = symArray.find((s) => {
          const name = typeof s.name === 'string' ? s.name : (typeof s.symbolName === 'string' ? s.symbolName : '');
          return name.toUpperCase() === strategy.symbol.toUpperCase();
        });
        if (found) {
          const sid = typeof found.symbolId === 'number' ? found.symbolId : (typeof found.symbolId === 'string' ? parseInt(found.symbolId, 10) : 0);
          const digits = typeof found.digits === 'number' ? found.digits : 5;
          setSymbolInfo({ symbolId: sid, name: strategy.symbol, digits });
        } else {
          setSymbolInfo(null);
        }
      }
    } catch { /* ignore */ }
    setSymbolLoading(false);
  }, [selectedAccountId, strategy.symbol]);

  useEffect(() => {
    if (gatewayConnected && selectedAccountId) {
      resolveSymbol();
    }
  }, [gatewayConnected, selectedAccountId, resolveSymbol]);

  // ─── Fetch candles ──────────────────────────────────────────

  const fetchCandles = useCallback(async () => {
    if (!selectedAccountId || !symbolInfo) return;

    setCandleLoading(true);
    setCandleError(null);

    const period = TIMEFRAME_PERIOD[strategy.timeframe] || 60;
    const now = Date.now();
    const fromTs = now - (period === 60 ? 20 : period === 240 ? 60 : 90) * 24 * 60 * 60 * 1000;

    try {
      const { data } = await supabase.functions.invoke<{
        success: boolean;
        trendbars: Record<string, unknown> | null;
        error?: string;
      }>('ctrader-gateway', {
        method: 'POST',
        body: {
          action: 'get-trendbars',
          accountId: selectedAccountId,
          symbolId: symbolInfo.symbolId,
          period,
          fromTimestamp: fromTs,
          toTimestamp: now,
        },
      });

      if (data?.success && data.trendbars) {
        const parsed = parseTrendbars(data.trendbars, symbolInfo.digits);
        setCandles(parsed);
      } else {
        setCandleError(data?.error || 'Khong the tai du lieu nen');
      }
    } catch (err) {
      setCandleError(err instanceof Error ? err.message : 'Loi tai nen');
    } finally {
      setCandleLoading(false);
      setLastUpdated(Date.now());
    }
  }, [selectedAccountId, symbolInfo, strategy.timeframe]);

  useEffect(() => {
    if (symbolInfo) {
      fetchCandles();
    }
  }, [symbolInfo, fetchCandles]);

  // ─── Poll spot prices ────────────────────────────────────────

  const fetchSpotPrice = useCallback(async () => {
    if (!selectedAccountId || !symbolInfo) return;
    try {
      const { data } = await supabase.functions.invoke<{
        success: boolean;
        spotPrices: Record<number, { bid: number | null; ask: number | null }>;
      }>('ctrader-gateway', {
        method: 'POST',
        body: {
          action: 'get-spot-prices',
          accountId: selectedAccountId,
          symbolIds: [symbolInfo.symbolId],
        },
      });
      if (data?.success && data.spotPrices[symbolInfo.symbolId]) {
        const sp = data.spotPrices[symbolInfo.symbolId];
        setSpotPrice({
          bid: sp.bid,
          ask: sp.ask,
          timestamp: Date.now(),
        });
      }
    } catch { /* ignore */ }
  }, [selectedAccountId, symbolInfo]);

  useEffect(() => {
    if (symbolInfo) {
      fetchSpotPrice();
    }
  }, [symbolInfo, fetchSpotPrice]);

  // ─── Auto refresh ───────────────────────────────────────────

  useEffect(() => {
    if (!autoRefresh) {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (spotPollRef.current) clearInterval(spotPollRef.current);
      return;
    }

    refreshIntervalRef.current = setInterval(() => {
      if (symbolInfo) fetchCandles();
    }, 30000);

    spotPollRef.current = setInterval(() => {
      if (symbolInfo) fetchSpotPrice();
    }, 5000);

    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (spotPollRef.current) clearInterval(spotPollRef.current);
    };
  }, [autoRefresh, symbolInfo, fetchCandles, fetchSpotPrice]);

  // ─── Compute signal ─────────────────────────────────────────

  const currentPrice = useMemo(() => {
    if (spotPrice?.bid !== null && spotPrice.bid !== undefined) return spotPrice.bid;
    if (candles.length > 0) return candles[candles.length - 1].close;
    return 0;
  }, [spotPrice, candles]);

  useEffect(() => {
    if (candles.length < 50 || currentPrice === 0) return;

    const result = evaluateStrategySignal(
      candles,
      strategy.indicators,
      strategy.entry_rules,
      strategy.exit_rules,
      currentPrice,
      symbolInfo?.digits || 5,
      strategy.stop_loss_pips,
      strategy.take_profit_pips,
    );
    setSignalResult(result);
  }, [candles, currentPrice, strategy, symbolInfo?.digits]);

  // ─── Lot size calculation ───────────────────────────────────

  const selectedAccount = gatewayAccounts.find(
    (a) => Number(a.account_id) === selectedAccountId || a.id === selectedAccountId,
  );

  const recommendedLot = useMemo(() => {
    if (!selectedAccount || !signalResult) return 0;
    return calculateLotSize(
      selectedAccount.balance,
      strategy.risk_per_trade,
      strategy.stop_loss_pips,
    );
  }, [selectedAccount, signalResult, strategy.risk_per_trade, strategy.stop_loss_pips]);

  // ─── Render ──────────────────────────────────────────────────

  if (!gatewayConnected) {
    return (
      <div className="bg-background-100 rounded-xl border border-background-200/70 p-8 text-center">
        <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
          <i className="ri-link-unlink text-amber-400 text-xl"></i>
        </div>
        <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2">cTrader chua ket noi</h3>
        <p className="text-xs text-foreground-500 mb-4 max-w-sm mx-auto">
          Hay vao trang Home de ket noi cTrader, sau do quay lai day de xem tin hieu real-time tu chien luoc.
        </p>
        <button
          type="button"
          onClick={fetchGatewayStatus}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-1"></i>
          Kiem tra lai
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center">
            <i className="ri-radar-line text-accent-400 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground-900">Live Signal Monitor</h3>
            <p className="text-[11px] text-foreground-500">
              {strategy.symbol} · {strategy.timeframe} · {strategy.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Account selector */}
          <select
            value={selectedAccountId ?? ''}
            onChange={(e) => setSelectedAccountId(e.target.value ? parseInt(e.target.value, 10) : null)}
            className="px-3 py-2 bg-background-50 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none focus:border-primary-500/50 cursor-pointer"
          >
            <option value="">Chon tai khoan</option>
            {gatewayAccounts.map((acc) => (
              <option key={acc.id} value={acc.account_id}>
                {acc.account_name} ({acc.is_live ? 'Live' : 'Demo'})
              </option>
            ))}
          </select>

          {/* Auto refresh toggle */}
          <button
            type="button"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={cn(
              'px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 border',
              autoRefresh
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : 'bg-background-100 text-foreground-500 border-background-300/60',
            )}
          >
            <i className={autoRefresh ? 'ri-pulse-line animate-pulse' : 'ri-pause-circle-line'}></i>
            {autoRefresh ? 'Auto ON' : 'Auto OFF'}
          </button>

          <button
            type="button"
            onClick={() => { fetchCandles(); fetchSpotPrice(); }}
            disabled={candleLoading || symbolLoading}
            className="px-3 py-2 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
          >
            {candleLoading ? (
              <><i className="ri-loader-4-line animate-spin"></i> Dang tai...</>
            ) : (
              <><i className="ri-refresh-line"></i> Cap nhat</>
            )}
          </button>
        </div>
      </div>

      {/* Symbol status */}
      {symbolLoading && (
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-loader-4-line animate-spin"></i>
          Dang tim symbol {strategy.symbol}...
        </div>
      )}

      {symbolInfo && (
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <i className="ri-check-line"></i>
            Symbol #{symbolInfo.symbolId} · {symbolInfo.digits} digits
          </span>
          <span className="text-foreground-400">·</span>
          <span>{candles.length} nen lich su</span>
          {lastUpdated > 0 && (
            <>
              <span className="text-foreground-400">·</span>
              <span>Cap nhat: {formatTime(lastUpdated)}</span>
            </>
          )}
        </div>
      )}

      {!symbolInfo && !symbolLoading && gatewayConnected && (
        <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-3 flex items-start gap-2">
          <i className="ri-error-warning-line text-amber-400 text-xs mt-0.5"></i>
          <p className="text-amber-600 text-xs">Khong tim thay symbol {strategy.symbol} tren tai khoan cTrader. Hay chon tai khoan khac hoac kiem tra lai.</p>
        </div>
      )}

      {candleError && (
        <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-400 text-xs mt-0.5"></i>
          <p className="text-red-400 text-xs">{candleError}</p>
        </div>
      )}

      {/* Signal Card */}
      {signalResult && candles.length >= 50 && (
        <div className={cn(
          'rounded-xl border p-5 transition-all',
          signalResult.direction === 'BUY'
            ? 'bg-emerald-500/5 border-emerald-500/20'
            : signalResult.direction === 'SELL'
              ? 'bg-red-500/5 border-red-500/20'
              : 'bg-background-100 border-background-200/70',
        )}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center',
                signalResult.direction === 'BUY'
                  ? 'bg-emerald-500/10'
                  : signalResult.direction === 'SELL'
                    ? 'bg-red-500/10'
                    : 'bg-background-200/70',
              )}>
                <i className={cn(
                  'text-xl',
                  signalResult.direction === 'BUY'
                    ? 'ri-arrow-up-circle-line text-emerald-400'
                    : signalResult.direction === 'SELL'
                      ? 'ri-arrow-down-circle-line text-red-400'
                      : 'ri-subtract-line text-foreground-400',
                )}></i>
              </div>
              <div>
                <h4 className={cn(
                  'text-lg font-heading font-bold',
                  signalResult.direction === 'BUY'
                    ? 'text-emerald-400'
                    : signalResult.direction === 'SELL'
                      ? 'text-red-400'
                      : 'text-foreground-500',
                )}>
                  {signalResult.direction === 'BUY'
                    ? 'MUA'
                    : signalResult.direction === 'SELL'
                      ? 'BAN'
                      : 'TRUNG TINH'}
                </h4>
                <p className="text-[11px] text-foreground-500">
                  Do manh tin hieu: {signalResult.strength}/100
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-mono font-semibold text-foreground-800">
                {formatPrice(spotPrice?.bid, symbolInfo?.digits)} / {formatPrice(spotPrice?.ask, symbolInfo?.digits)}
              </p>
              <p className="text-[10px] text-foreground-400">
                Bid / Ask
              </p>
            </div>
          </div>

          {/* Entry / Exit reasons */}
          {signalResult.entryReasons.length > 0 && (
            <div className="mb-3 space-y-1">
              {signalResult.entryReasons.map((reason, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs">
                  <i className={cn(
                    'mt-0.5',
                    signalResult.direction === 'BUY'
                      ? 'ri-arrow-up-line text-emerald-400'
                      : signalResult.direction === 'SELL'
                        ? 'ri-arrow-down-line text-red-400'
                        : 'ri-information-line text-foreground-400',
                  )}></i>
                  <span className="text-foreground-700">{reason}</span>
                </div>
              ))}
            </div>
          )}

          {signalResult.exitReasons.length > 0 && (
            <div className="mb-3 space-y-1">
              <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-wide mb-1">Canh bao thoat lenh</p>
              {signalResult.exitReasons.map((reason, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs">
                  <i className="ri-error-warning-line text-amber-400 mt-0.5"></i>
                  <span className="text-foreground-700">{reason}</span>
                </div>
              ))}
            </div>
          )}

          {/* TP/SL Projection */}
          {signalResult.direction !== 'NEUTRAL' && (
            <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-background-200/40">
              <div className="text-center">
                <p className="text-[10px] text-foreground-500 mb-0.5">Take Profit</p>
                <p className="text-sm font-mono font-semibold text-emerald-400">
                  {formatPrice(signalResult.tpLevel, symbolInfo?.digits)}
                </p>
                <p className="text-[10px] text-foreground-400">+{strategy.take_profit_pips} pips</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-foreground-500 mb-0.5">Gia hien tai</p>
                <p className="text-sm font-mono font-semibold text-foreground-800">
                  {formatPrice(signalResult.currentPrice, symbolInfo?.digits)}
                </p>
                <p className="text-[10px] text-foreground-400">Bid</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-foreground-500 mb-0.5">Stop Loss</p>
                <p className="text-sm font-mono font-semibold text-red-400">
                  {formatPrice(signalResult.slLevel, symbolInfo?.digits)}
                </p>
                <p className="text-[10px] text-foreground-400">-{strategy.stop_loss_pips} pips</p>
              </div>
            </div>
          )}

          {/* Lot size */}
          {recommendedLot > 0 && signalResult.direction !== 'NEUTRAL' && selectedAccount && (
            <div className="mt-4 pt-4 border-t border-background-200/40 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-foreground-500">Khuyen nghi lot size</p>
                <p className="text-sm font-mono font-semibold text-foreground-800">{recommendedLot.toFixed(2)} lots</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-foreground-500">Rui ro</p>
                <p className="text-sm font-mono font-semibold text-foreground-800">
                  ${(selectedAccount.balance * strategy.risk_per_trade / 100).toFixed(2)} ({strategy.risk_per_trade}%)
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Indicator Table */}
      {signalResult && signalResult.indicators.length > 0 && (
        <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
            <h4 className="text-xs font-heading font-semibold text-foreground-800 flex items-center gap-1.5">
              <i className="ri-line-chart-line text-accent-400 text-xs"></i>
              Chi so ky thuat vs Gia hien tai
            </h4>
            <span className="text-[10px] text-foreground-400">Khoang cach tinh bang pips</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-background-200/40 bg-background-50">
                  <th className="text-left py-2 px-4 text-foreground-500 font-medium text-[10px] uppercase">Indicator</th>
                  <th className="text-right py-2 px-4 text-foreground-500 font-medium text-[10px] uppercase">Gia tri</th>
                  <th className="text-right py-2 px-4 text-foreground-500 font-medium text-[10px] uppercase">Truoc do</th>
                  <th className="text-right py-2 px-4 text-foreground-500 font-medium text-[10px] uppercase">Cach gia</th>
                </tr>
              </thead>
              <tbody>
                {signalResult.indicators.map((ind, idx) => (
                  <tr key={idx} className="border-b border-background-200/20 hover:bg-background-50 transition-colors">
                    <td className="py-2 px-4 font-mono text-foreground-700">{ind.label}</td>
                    <td className="py-2 px-4 text-right font-mono font-medium text-foreground-800">
                      {formatPrice(ind.currentValue, symbolInfo?.digits)}
                    </td>
                    <td className="py-2 px-4 text-right font-mono text-foreground-500">
                      {formatPrice(ind.previousValue, symbolInfo?.digits)}
                    </td>
                    <td className="py-2 px-4 text-right font-mono">
                      {ind.distancePips !== null ? (
                        <span className={cn(
                          'font-medium',
                          Math.abs(ind.distancePips) <= 3
                            ? 'text-amber-400'
                            : ind.distancePips > 0
                              ? 'text-emerald-400'
                              : 'text-red-400',
                        )}>
                          {ind.distancePips > 0 ? '+' : ''}{ind.distancePips.toFixed(1)} pips
                        </span>
                      ) : (
                        <span className="text-foreground-400">\u2014</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Not enough candles */}
      {candles.length > 0 && candles.length < 50 && !candleLoading && (
        <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-time-line text-amber-400 text-sm mt-0.5"></i>
          <div>
            <p className="text-xs font-medium text-amber-700">Dang tai du lieu...</p>
            <p className="text-[11px] text-amber-600 mt-0.5">
              Hien tai co {candles.length} nen, can toi thieu 50 nen de tinh chi so. Vui long doi them giay lat.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}